import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Typography, Button } from '../../../../../design-system';

const spin = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const WelcomeHeader = styled.div`
  display: flex;
  align-items: center;
  animation: ${fadeInUp} 0.6s ease-out forwards;
  color: #1A73E8;

  i {
    font-size: 28px;
    margin-right: 14px;
  }
`;

const GlassContainer = styled.div`
  /* From https://css.glass */
  background: rgba(255, 255, 255, 0.85);
  border-radius: 28px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 12px;
  
  &.success {
    background-color: #E6F4EA;
    color: #137333;
  }
  
  &.warning {
    background-color: #E8EAED;
    color: #5F6368;
  }
  
  &.hourglass {
    background-color: #E8EAED;
    color: #5F6368;
  }
  
  &.spinning {
    color: #1A73E8;
    animation: ${spin} 1s linear infinite;
  }
  
  &.pending {
    color: #80868B;
    border: 2px solid #DADCE0;
    width: 20px;
    height: 20px;
  }
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  opacity: ${props => props.active ? 1 : 0.6};
  transition: all 0.3s ease;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background-color: #E8EAED;
  border-radius: 4px;
  margin-top: 24px;
  margin-bottom: 8px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: #1A73E8;
  width: ${props => props.progress}%;
  transition: width 0.5s ease-in-out;
  border-radius: 4px;
`;

export default function ProgressPanel({ progressValue, progressStatus, onUploadCertificate, isFiscal }) {
    
    // progressStatus can be: 'idle', 'creating_branch', 'adjusting', 'waiting_certificate', 'certificate_done', 'done'
    
    const steps = [
        {
            id: 'branch',
            label: 'Empresa cadastrada',
            isDone: progressValue >= 50,
            isActive: progressValue > 0 && progressValue < 50
        },
        {
            id: 'adjustments',
            label: progressValue >= 85 ? 'Ajustes finais concluídos' : 'Realizando ajustes finais...',
            isDone: progressValue >= 85,
            isActive: progressValue >= 50 && progressValue < 85
        }
    ];

    if (isFiscal) {
        const isWaitingCert = progressStatus === 'waiting_certificate';
        steps.push({
            id: 'certificate',
            label: isWaitingCert ? 'Cadastro do certificado digital necessário' : 'Certificado digital cadastrado',
            isDone: progressValue >= 100 && !isWaitingCert,
            isHourglass: isWaitingCert,
            isActive: progressValue >= 80 && progressValue < 100
        });
    } else {
        // If not fiscal, progress skips certificate directly to 100% after adjustments
        if (progressValue >= 85 && progressValue < 100 && progressStatus === 'adjusting') {
            // Handled externally, but let's just make sure UI reflects logic
        }
    }

    return (
        <GlassContainer>
            <div className="p-4" style={{ minHeight: '300px' }}>
                {(progressStatus === 'done' || progressStatus === 'waiting_certificate') ? (
                    <WelcomeHeader className="mb-4">
                        <i className="fa fa-handshake-o"></i>
                        <Typography variant="h3" style={{ margin: 0, color: 'inherit', fontWeight: 600 }}>
                            Seja muito bem-vindo ao EasyGestor!
                        </Typography>
                    </WelcomeHeader>
                ) : (
                    <Typography variant="h3" className="mb-4">Configurando empresa</Typography>
                )}
                
                <div className="my-4">
                    {steps.map(step => (
                        <StepItem key={step.id} active={step.isActive || step.isDone || step.isHourglass}>
                            {step.isDone ? (
                                <IconWrapper className="success">
                                    <i className="fa fa-check" style={{ fontSize: '12px' }}></i>
                                </IconWrapper>
                            ) : step.isHourglass ? (
                                <IconWrapper className="hourglass">
                                    <i className="fa fa-hourglass-half" style={{ fontSize: '11px' }}></i>
                                </IconWrapper>
                            ) : step.isActive ? (
                                <IconWrapper className="spinning">
                                    <i className="fa fa-circle-o-notch"></i>
                                </IconWrapper>
                            ) : (
                                <IconWrapper className="pending" />
                            )}
                            <Typography variant="body1" style={{ fontWeight: step.isActive ? 600 : 400, marginLeft: step.isActive ? '4px' : '0', transition: 'all 0.3s' }}>
                                {step.label}
                            </Typography>
                        </StepItem>
                    ))}
                </div>

                <ProgressBarContainer>
                    <ProgressBarFill progress={progressValue} />
                </ProgressBarContainer>
                
                <div className={`d-flex ${progressValue >= 80 ? 'justify-content-center' : 'justify-content-between'} align-items-center mt-2 mb-4`}>
                    {progressValue >= 100 ? (
                        <Typography variant="body1" style={{ fontWeight: 700, color: '#137333', fontSize: '1.1rem', textAlign: 'center', width: '100%' }}>
                            Empresa configurada com sucesso!
                        </Typography>
                    ) : progressValue === 80 ? (
                        <Typography variant="body1" style={{ fontWeight: 700, color: '#1A73E8', fontSize: '1.1rem', textAlign: 'center', width: '100%' }}>
                            Cadastro inicial concluído com sucesso
                        </Typography>
                    ) : (
                        <Typography variant="caption" color="var(--text-secondary)">
                            {progressValue}% concluído
                        </Typography>
                    )}
                </div>

                {progressStatus === 'waiting_certificate' && isFiscal && (
                    <div className="mt-4 pt-4 border-top text-center animate__animated animate__fadeIn">
                        <Typography variant="body1" color="var(--text-primary)" className="mb-3" style={{ fontSize: '1rem' }}>
                            Sua empresa foi cadastrada com sucesso. Para concluir a configuração, é necessário cadastrar o certificado digital.
                        </Typography>
                        <Typography variant="body2" color="var(--text-secondary)">
                            Você será redirecionado automaticamente em alguns segundos...
                        </Typography>
                    </div>
                )}
                
                {progressStatus === 'done' && (
                    <div className="mt-4 pt-4 border-top text-center animate__animated animate__fadeIn">
                        <Typography variant="body1" color="var(--text-secondary)">
                            Redirecionando para a seleção de planos em alguns segundos...
                        </Typography>
                    </div>
                )}
            </div>
        </GlassContainer>
    );
}



// WEBPACK FOOTER //
// ./src/views/AppViews/Branches/CreateRefactored/components/ProgressPanel.jsx